import { Link as RouterLink, type Href } from 'expo-router';
import { Pencil, MapPin, Stethoscope, Hospital } from 'lucide-react-native';
import { type ReactNode } from 'react';
import { Pressable, Text as RNText } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { ExpertMetrics, useHasExpertMetrics } from '@/components/profile/ExpertMetrics';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { SPECIALTY_BADGE_CLASS, SPECIALTY_BADGE_TEXT_CLASS } from '@/constants/badges';
import type { UserV2 } from '@/services/getUserV2';
import { getAvatarUrl } from '@/util/getAvatarUrl';

type UserProfileData = Partial<UserV2> & { id?: number };

type InfoItem = { key: string; icon: any; text: string };

interface ProfessionalSectionProps {
  userData?: UserProfileData | null;
  isOwnProfile: boolean;
  nameWithoutDoctor: string;
  responseTimeLabel: string;
  cancerFocusAreas: string;
  subspecialtyItems: string[];
  onEditSummary: () => void;
  /**
   * The screen's header carries the edit action, so the section leaves its own pencil out
   * rather than show two. The native profile page does this.
   */
  isEditInHeader?: boolean;
  onSendMessage: () => void;
  messageUrl: Href;
  showMessageButton?: boolean;
  /** Optional content rendered below the focus areas block. */
  chipSlot?: ReactNode;
}

/**
 * Lifts the whole metrics row onto `background-0` against the header's `background-50`, the
 * same pairing `referenceChampionBadge.tsx` uses for the badge alone, applied here to response
 * time and the badge together. One shared strip rather than the badge's own `elevated` lift,
 * since response time has no lift of its own — the badge alone lighting up while response time
 * stayed bare beside it was the "separated" look this replaces. No border: white on
 * `background-50` is elevation, not a control boundary, and pairs at 1.08:1, enough to group a
 * static row without implying either metric is pressable on its own.
 *
 * Fills the content column on phones instead of hugging its contents. Hugging leaves the strip
 * ending short of the info items directly above it, which reads as a stray box rather than as
 * the base of the same column, and the narrower the metrics get the more it shows. From `md` it
 * hugs again, where the column is wide enough that filling it would be a broad empty band.
 *
 * An explicit width rather than `self-stretch`: align-self did not fill on Android, where a
 * definite width is the dependable way to reach the parent's edges.
 */
const METRICS_STRIP = 'w-full md:w-auto md:self-start rounded-md bg-background-0 px-3 py-2';

const ProfileBadges = ({ userData }: { userData: UserProfileData | null | undefined }) => (
  <HStack className="items-center gap-2">
    {userData?.specialty && (
      <Badge action="info" className={twJoin('h-7', SPECIALTY_BADGE_CLASS)}>
        <BadgeText className={SPECIALTY_BADGE_TEXT_CLASS}>{userData.specialty}</BadgeText>
      </Badge>
    )}
    {userData?.userType === 'expert' && (
      <Badge action="muted" className="h-7 border">
        <BadgeText className="font-normal text-xs text-typography-800">PRIMUM EXPERT</BadgeText>
      </Badge>
    )}
  </HStack>
);

const ProfileAvatarAndName = ({
  userData,
  nameWithoutDoctor,
  isCentered = true,
  includeBadges = true,
}: {
  userData: UserProfileData | null | undefined;
  nameWithoutDoctor: string;
  isCentered?: boolean;
  includeBadges?: boolean;
}) => (
  <>
    <Avatar size="2xl" className="h-28 w-28">
      <AvatarImage
        source={getAvatarUrl(userData?.avatar, true, { large: true }) || primum_default_avatar}
      />
    </Avatar>
    <VStack className="items-start gap-2 md:flex-1">
      <RNText
        className={twJoin(
          'font-semibold text-xl text-typography-900',
          isCentered
            ? 'self-center text-center'
            : 'self-center text-center md:self-start md:text-left'
        )}>
        {nameWithoutDoctor}
      </RNText>
      {includeBadges && (
        <VStack className={isCentered ? 'self-center' : 'self-center md:self-start'}>
          <ProfileBadges userData={userData} />
        </VStack>
      )}
    </VStack>
  </>
);

const InfoItemRow = ({ item }: { item: InfoItem }) => (
  <HStack className="items-start gap-2">
    <Icon as={item.icon} size="md" className="shrink-0 text-typography-500" />
    <Text className="text-sm text-typography-800">{item.text}</Text>
  </HStack>
);

const InfoItemsList = ({
  items,
  layout = 'stacked',
  className = '',
}: {
  items: InfoItem[];
  layout?: 'stacked' | 'inline-with-pipes';
  className?: string;
}) => {
  const nonPositionItems = items.filter((item) => item.key !== 'position');

  const positionItem = items.find((item) => item.key === 'position');

  if (layout === 'inline-with-pipes') {
    return (
      <VStack className={className}>
        {nonPositionItems.length > 0 && (
          <HStack className="items-center gap-2">
            {nonPositionItems.map((item, idx, arr) => (
              <HStack key={item.key} className="items-center gap-2">
                <InfoItemRow item={item} />
                {idx < arr.length - 1 && (
                  <Divider orientation="vertical" className="h-4 bg-typography-400" />
                )}
              </HStack>
            ))}
          </HStack>
        )}
        {positionItem && (
          <HStack className="items-center gap-2 pt-1">
            <InfoItemRow item={positionItem} />
          </HStack>
        )}
      </VStack>
    );
  }

  return (
    <VStack className={className}>
      {nonPositionItems.map((item) => (
        <InfoItemRow key={item.key} item={item} />
      ))}
      {positionItem && (
        <HStack className="items-center gap-2 pt-1">
          <InfoItemRow item={positionItem} />
        </HStack>
      )}
    </VStack>
  );
};

/**
 * ProfessionalSection - Profile professional information display component
 *
 * Renders a comprehensive professional profile section with avatar, badges, and detailed information.
 * Features include:
 * - Responsive layout with mobile and desktop variants
 * - Professional badges (specialty, expert status)
 * - Dynamic info items (institution, location, position)
 * - Response time and the Reference Champion badge, grouped as one metrics row
 * - Focus areas and subspecialties display
 * - Conditional edit and message buttons
 * - Avatar with fallback to default image
 * - Cross-platform responsive design
 */
export const ProfessionalSection = ({
  userData,
  isOwnProfile,
  nameWithoutDoctor,
  responseTimeLabel,
  cancerFocusAreas,
  subspecialtyItems,
  onEditSummary,
  isEditInHeader = false,
  onSendMessage,
  messageUrl,
  showMessageButton = true,
  chipSlot,
}: ProfessionalSectionProps) => {
  const locationString = [userData?.city, userData?.state].filter(Boolean).join(', ');

  /**
   * Response time no longer counts toward `infoItems`, so the mobile branch's wrapping block
   * needs its own signal for whether there's anything to show there — otherwise an expert with
   * no institution, location, or position but a response time or badge would lose the whole
   * block, metrics row included.
   */
  const hasExpertMetrics = useHasExpertMetrics({
    expertId: userData?.id ?? '',
    responseTimeLabel,
  });

  const infoItems: InfoItem[] = [
    userData?.institution && {
      key: 'institution',
      icon: Hospital,
      text: userData.institution,
    },
    locationString && {
      key: 'location',
      icon: MapPin,
      text: locationString,
    },
    userData?.position && {
      key: 'position',
      icon: Stethoscope,
      text: userData.position,
    },
  ].filter(Boolean) as InfoItem[];

  return (
    <VStack className="w-full bg-background-50">
      <VStack className="relative mx-auto w-full max-w-5xl">
        {isOwnProfile && !isEditInHeader && (
          <Pressable
            onPress={onEditSummary}
            className="absolute right-6 top-0 z-10 p-2"
            hitSlop={{ top: 15, bottom: 15, left: 15, right: 15 }}>
            <Icon as={Pencil} size="md" className="text-primary-600" />
          </Pressable>
        )}

        <VStack className="block md:hidden">
          <VStack className="w-full items-center gap-4 px-6 pt-6">
            <ProfileAvatarAndName
              userData={userData}
              nameWithoutDoctor={nameWithoutDoctor}
              isCentered
            />
          </VStack>

          <VStack className="mx-auto w-full max-w-5xl px-6">
            <Divider className="my-4 w-full bg-background-400 md:my-6" />
          </VStack>

          {(infoItems.length > 0 || hasExpertMetrics) && (
            <VStack className="mx-auto w-full max-w-5xl gap-4 px-6 pb-4 md:pb-6">
              {infoItems.length > 0 && (
                <InfoItemsList items={infoItems} className="w-full items-start gap-1" />
              )}

              {/*
               * Response time and the badge share one strip instead of response time sitting in
               * the icon list above: they're both measured facts about the expert, and splitting
               * them across two unrelated locations on the page was the bug this fixes. Sits on
               * the container edge, not indented into the icon rows' text column — that column
               * is sized for icon-led text, and this strip's own box would land on the column
               * while its padding pushed the content off it.
               */}
              {hasExpertMetrics && (
                <VStack className={METRICS_STRIP}>
                  <ExpertMetrics
                    expertId={userData?.id ?? ''}
                    responseTimeLabel={responseTimeLabel}
                    size="text-sm"
                    explain
                    explainSource="profile"
                    celebrate={isOwnProfile}
                  />
                </VStack>
              )}
            </VStack>
          )}
        </VStack>

        <VStack className="hidden md:block">
          <VStack className="w-full items-center gap-4 px-6 pt-4 md:flex-row md:items-start md:gap-6 md:pt-5">
            <Avatar size="2xl" className="h-28 w-28">
              <AvatarImage
                source={
                  getAvatarUrl(userData?.avatar, true, { large: true }) || primum_default_avatar
                }
              />
            </Avatar>

            <VStack className="flex-1 items-start gap-2">
              <RNText className="self-center text-center font-semibold text-xl text-typography-900 md:self-start md:text-left">
                {nameWithoutDoctor}
              </RNText>

              <VStack className="self-center md:self-start">
                <ProfileBadges userData={userData} />
              </VStack>

              {infoItems.length > 0 && (
                <InfoItemsList items={infoItems} layout="inline-with-pipes" className="w-full" />
              )}

              {/* Shared strip and container edge, for the reasons given on the mobile branch above. */}
              {hasExpertMetrics && (
                <VStack className={METRICS_STRIP}>
                  <ExpertMetrics
                    expertId={userData?.id ?? ''}
                    responseTimeLabel={responseTimeLabel}
                    size="text-sm"
                    explain
                    explainSource="profile"
                    celebrate={isOwnProfile}
                  />
                </VStack>
              )}
            </VStack>
          </VStack>

          <VStack className="mx-auto w-full max-w-5xl px-6">
            <Divider className="my-6 w-full bg-background-400" />
          </VStack>
        </VStack>
      </VStack>

      <VStack className="mx-auto w-full max-w-5xl gap-4 px-6 pb-6 md:flex-row md:items-start md:justify-between md:pb-9">
        <VStack className="gap-2 md:flex-1">
          {cancerFocusAreas && (
            <Text className="font-normal text-base text-typography-800">
              Focus Areas: <Text className="font-semibold">{cancerFocusAreas}</Text>
            </Text>
          )}

          {subspecialtyItems.length > 0 && userData?.userType !== 'expert' && (
            <Text className="font-normal text-sm text-typography-800 md:text-base">
              Subspecialties: {subspecialtyItems.join(', ')}
            </Text>
          )}

          {chipSlot ? <VStack className="mt-1">{chipSlot}</VStack> : null}
        </VStack>

        {showMessageButton && userData?.id && !isOwnProfile && userData?.userType === 'expert' && (
          <RouterLink href={messageUrl} asChild>
            <Button onPress={onSendMessage} className="md:ml-4 md:shrink-0">
              <ButtonText>Message</ButtonText>
            </Button>
          </RouterLink>
        )}
      </VStack>

      <Divider />
    </VStack>
  );
};
